import { Button, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

const { Text } = Typography

interface TableResultCountProps {
  /** Rows the table is currently showing. */
  loaded: number
  /** Rows the server says the filtered list holds. */
  total: number
  /** A further page is on its way. */
  loading?: boolean
  /** The page the list would load next, if the last one did not fail. */
  next?: string | null
  /** The page a load-more failed on, offered back. */
  retryNext?: string | null
  onRetry?: () => void
}

/**
 * How much of the list is on screen, and what the list is doing. A paginated
 * table said this in its footer; an infinite-scrolling one has no footer, and
 * without it a user has no way to tell a list that has ended from one still
 * loading — or from one that stopped because a page failed.
 *
 * Rendered inside the table's card so `useTableFooterHeight` measures it and the
 * body is fitted above it. That is why this is one row of fixed height that
 * never wraps: several lists feed the measured height straight back into the
 * body's `scroll.y`, so a footer that grew a line would move the rows under the
 * reader.
 *
 * Plain `t()` rather than `<Trans>`: this sits under every converted list, and
 * interpolated markup would make each of their test suites carry a `Trans` mock.
 */
export function TableResultCount({
  loaded,
  total,
  loading,
  next,
  retryNext,
  onRetry
}: TableResultCountProps) {
  const { t } = useTranslation()

  if (!total) return null

  return (
    <div
      className="flex h-[46px] flex-nowrap items-center gap-2 px-4"
      aria-live="polite"
    >
      <Text type="secondary">
        {t("table.loadedResults", { loaded, total })}
      </Text>
      {/* The slot holds its place whether or not the indicator is in it, so a
          page that settles within a frame or two cannot add and remove an item
          from the row and twitch the line the reader is on. The ring is drawn
          here rather than taken from `Spin`, whose box this component cannot
          predict: `.ant-spin` is pinned to `display: flex` app-wide with its own
          centring, so it neither keeps antd's own size nor sits where a plain
          inline indicator would. */}
      <span className="flex size-4 shrink-0 items-center justify-center">
        {loading ? (
          <span
            role="status"
            aria-label={t("table.loadingMore")}
            className="size-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500"
          />
        ) : null}
      </span>
      {/* `next` decides, not `retryNext` alone: a slice that forgets to clear
          the failed url on a successful retry then shows a missing offer for one
          render rather than a stuck one for the session. The two are mutually
          exclusive by construction — a failure moves the url from one to the
          other. */}
      {!loading && !next && retryNext && onRetry ? (
        <>
          <Text type="danger">{t("table.loadMoreFailed")}</Text>
          <Button type="link" size="small" onClick={onRetry}>
            {t("table.loadMoreRetry")}
          </Button>
        </>
      ) : null}
    </div>
  )
}
